import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchSectionCode, createSectionCode, updateSectionCode, deleteSectionCode, fetchTiaData } from '../../api';
import { Button, Input, Select, message, Form, Card, Space, Modal, Divider } from 'antd';
import { SaveOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    TourInformationSection,
    TourContactListSection,
    ItinerarySection,
    TourAccommodationSection
} from '../../components/SectionForms';

const { Option } = Select;

// Transform itineraryData form structure to scheduleList template schema
const transformItineraryToScheduleList = (itineraryData: any[]) => {
    return itineraryData.map((val: any) => {
        const obj: any = {
            dayOfTrip: val.dayOfTrip,
        };
        
        if (val.coachPlateNumber) {
            obj.coachPlateNumber = val.coachPlateNumber;
        }
        
        if (val.itinerary) {
            obj.route = Object.values(val.itinerary).map((i: any) => {
                const route: any = {
                    fromTime: dayjs.isDayjs(i.time?.[0]) ? i.time[0].format("HH:mm") : i.time?.[0],
                    toTime: dayjs.isDayjs(i.time?.[1]) ? i.time[1].format("HH:mm") : i.time?.[1],
                    facilityType: i.facilityType,
                };
                
                if (i.facility === "自由活動" || i.facility === "其他") {
                    route.facility = i.facility + (i.otherFacility ? ", " + i.otherFacility : "");
                } else {
                    route.facility = i.facility;
                }
                
                return route;
            });
        }
        
        return obj;
    });
};

// Transform scheduleList template schema to itineraryData form structure
const transformScheduleListToItinerary = (scheduleList: any[]) => {
    return scheduleList.map((val: any, index: number) => {
        const itinerary: any = {};
        
        if (val.route) {
            val.route.forEach((r: any, i: number) => {
                if (r.facility && r.facility.includes(",")) {
                    const [mainFacility, otherFacility] = r.facility.split(",").map((s: string) => s.trim());
                    itinerary[`itinerary${i}`] = {
                        time: [
                            dayjs(r.fromTime, "HH:mm"),
                            dayjs(r.toTime, "HH:mm"),
                        ],
                        facilityType: r.facilityType,
                        facility: mainFacility,
                        otherFacility: otherFacility || null,
                    };
                } else {
                    itinerary[`itinerary${i}`] = {
                        time: [
                            dayjs(r.fromTime, "HH:mm"),
                            dayjs(r.toTime, "HH:mm"),
                        ],
                        facilityType: r.facilityType,
                        facility: r.facility,
                        otherFacility: null,
                    };
                }
            });
        }
        
        return {
            dayOfTrip: val.dayOfTrip,
            coachPlateNumber: val.coachPlateNumber || null,
            itinerary: val.route ? itinerary : null,
        };
    });
};

export default function SectionCodeDetail() {
    const { code } = useParams<{ code: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const [sectionForm] = Form.useForm();
    const [isNew, setIsNew] = useState(false);
    const [selectedType, setSelectedType] = useState<string>('');
    const [tiaData, setTiaData] = useState<any>(null);
    const [dataVersion, setDataVersion] = useState(0);

    useEffect(() => {
        if (!code || code === 'new') {
            setIsNew(true);
        } else {
            load();
        }
        loadTiaData();
    }, [code]);

    const loadTiaData = async () => {
        try {
            const data = await fetchTiaData();
            setTiaData(data);
        } catch (err) {
            console.error('Failed to load TIA data', err);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const resp = await fetchSectionCode(code as string);
            const result = await resp.json();
            if (result.success) {
                form.setFieldsValue({ 
                    code: result.data.code, 
                    type: result.data.type, 
                    name: result.data.name 
                });
                setSelectedType(result.data.type);
                
                // Populate section form with data
                const formData = result.data.data ? { ...result.data.data } : {};
                
                // Transform based on section type
                if (result.data.type === 'tourInfo') {
                    // Convert date strings (YYYY-MM-DD) to dayjs objects
                    if (formData.arrivalDate && typeof formData.arrivalDate === 'string') {
                        formData.arrivalDate = dayjs(formData.arrivalDate, 'YYYY-MM-DD');
                    }
                    if (formData.depatureDate && typeof formData.depatureDate === 'string') {
                        formData.depatureDate = dayjs(formData.depatureDate, 'YYYY-MM-DD');
                    }
                    // Convert time strings (HH:mm) to dayjs objects
                    if (formData.arrivalTime && typeof formData.arrivalTime === 'string') {
                        formData.arrivalTime = dayjs(formData.arrivalTime, 'HH:mm');
                    }
                    if (formData.depatureTime && typeof formData.depatureTime === 'string') {
                        formData.depatureTime = dayjs(formData.depatureTime, 'HH:mm');
                    }
                } else if (result.data.type === 'itinerary') {
                    // Convert date strings to dayjs objects
                    if (formData.arrivalDate && typeof formData.arrivalDate === 'string') {
                        formData.arrivalDate = dayjs(formData.arrivalDate, 'YYYY-MM-DD');
                    }
                    if (formData.depatureDate && typeof formData.depatureDate === 'string') {
                        formData.depatureDate = dayjs(formData.depatureDate, 'YYYY-MM-DD');
                    }
                    
                    // Transform scheduleList to itineraryData form structure
                    if (formData.scheduleList && Array.isArray(formData.scheduleList)) {
                        formData.itineraryData = transformScheduleListToItinerary(formData.scheduleList);
                        // Remove scheduleList from form data as it's not used in the form
                        delete formData.scheduleList;
                    }
                }
                
                // Set form values after transformation
                sectionForm.setFieldsValue(formData);
                // Increment version to force ItinerarySection to re-render and pick up new values
                setDataVersion(prev => prev + 1);
            } else {
                message.error('載入失敗');
            }
        } catch (err) {
            console.error(err);
            message.error('載入失敗');
        } finally { 
            setLoading(false); 
        }
    };

    const onSave = async () => {
        try {
            const values = await form.validateFields();
            const sectionData = { ...sectionForm.getFieldsValue(true) };

            // Transform data based on section type to match template schema
            if (values.type === 'tourInfo') {
                // Convert dayjs dates to YYYY-MM-DD format
                if (dayjs.isDayjs(sectionData.arrivalDate)) {
                    sectionData.arrivalDate = sectionData.arrivalDate.format('YYYY-MM-DD');
                }
                if (dayjs.isDayjs(sectionData.depatureDate)) {
                    sectionData.depatureDate = sectionData.depatureDate.format('YYYY-MM-DD');
                }
                // Convert arrivalTime and depatureTime if they are dayjs objects
                if (dayjs.isDayjs(sectionData.arrivalTime)) {
                    sectionData.arrivalTime = sectionData.arrivalTime.format('HH:mm');
                }
                if (dayjs.isDayjs(sectionData.depatureTime)) {
                    sectionData.depatureTime = sectionData.depatureTime.format('HH:mm');
                }
            } else if (values.type === 'itinerary') {
                // Convert dates to YYYY-MM-DD format
                if (dayjs.isDayjs(sectionData.arrivalDate)) {
                    sectionData.arrivalDate = sectionData.arrivalDate.format('YYYY-MM-DD');
                }
                if (dayjs.isDayjs(sectionData.depatureDate)) {
                    sectionData.depatureDate = sectionData.depatureDate.format('YYYY-MM-DD');
                }
                
                // Transform itineraryData to scheduleList format
                if (sectionData.itineraryData && Array.isArray(sectionData.itineraryData)) {
                    sectionData.scheduleList = transformItineraryToScheduleList(sectionData.itineraryData);
                    // Remove itineraryData as it's just form structure
                    delete sectionData.itineraryData;
                }
            }

            const payload = {
                code: values.code,
                type: values.type,
                name: values.name,
                data: sectionData || {}
            };

            setSaving(true);
            
            if (isNew) {
                const resp = await createSectionCode(payload);
                const result = await resp.json();
                if (result.success) {
                    message.success('新增成功');
                    navigate('/section-codes');
                } else {
                    message.error(result.message || '新增失敗');
                }
            } else {
                const resp = await updateSectionCode(values.code, { name: values.name, data: payload.data });
                const result = await resp.json();
                if (result.success) {
                    message.success('更新成功');
                    navigate('/section-codes');
                } else {
                    message.error(result.message || '更新失敗');
                }
            }
        } catch (err) {
            console.error(err);
            message.error('資料驗證失敗，請檢查必填欄位');
        } finally {
            setSaving(false);
        }
    };

    const onDelete = () => {
        Modal.confirm({
            title: '確認刪除',
            content: `確定要刪除代碼 "${code}" 嗎？此操作無法復原。`,
            okText: '確認',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                if (!code) return;
                try {
                    const resp = await deleteSectionCode(code);
                    const result = await resp.json();
                    if (result.success) {
                        message.success('刪除成功');
                        navigate('/section-codes');
                    } else {
                        message.error(result.message || '刪除失敗');
                    }
                } catch (err) {
                    console.error(err);
                    message.error('刪除失敗');
                }
            },
        });
    };

    const onTypeChange = (type: string) => {
        setSelectedType(type);
        // Clear section form when type changes
        sectionForm.resetFields();
    };

    const renderSectionForm = () => {
        if (!selectedType) {
            return (
                <div style={{ 
                    padding: '40px', 
                    textAlign: 'center', 
                    color: '#999',
                    backgroundColor: '#fafafa',
                    borderRadius: '4px'
                }}>
                    請先選擇區段類型
                </div>
            );
        }

        switch (selectedType) {
            case 'tourInfo':
                return <TourInformationSection form={sectionForm} tiaData={tiaData} />;
            case 'contactList':
                return <TourContactListSection form={sectionForm} />;
            case 'itinerary':
                return <ItinerarySection key={dataVersion} form={sectionForm} tiaData={tiaData} />;
            case 'accommodation':
                return <TourAccommodationSection form={sectionForm} tiaData={tiaData} />;
            default:
                return null;
        }
    };

    return (
        <div style={{ 
            padding: '24px',
            backgroundColor: '#f5f7fa',
            minHeight: 'calc(100vh - 80px)'
        }}>
            <Card 
                title={
                    <Space>
                        <Button 
                            icon={<ArrowLeftOutlined />} 
                            onClick={() => navigate('/section-codes')}
                            type="text"
                        />
                        <span style={{ fontSize: '18px', fontWeight: 600 }}>
                            {isNew ? '新增區段代碼' : '編輯區段代碼'}
                        </span>
                    </Space>
                }
                bordered={false}
                loading={loading}
                style={{ 
                    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03), 0 1px 6px -1px rgba(0,0,0,0.02), 0 2px 4px 0 rgba(0,0,0,0.02)'
                }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item 
                        name="code" 
                        label="代碼" 
                        rules={[{ required: true, message: '請輸入代碼' }]}
                        extra={isNew ? "代碼必須唯一，建議使用格式：TA-001, TOUR-001 等" : undefined}
                    >
                        <Input 
                            disabled={!isNew} 
                            placeholder="例如: TA-001"
                            style={{ maxWidth: 300 }}
                        />
                    </Form.Item>
                    <Form.Item 
                        name="type" 
                        label="類型" 
                        rules={[{ required: true, message: '請選擇類型' }]}
                    >
                        <Select 
                            placeholder="選擇區段類型"
                            onChange={onTypeChange}
                            disabled={!isNew}
                            style={{ maxWidth: 300 }}
                        >
                            <Option value="tourInfo">旅行團資料</Option>
                            <Option value="contactList">聯絡人名單</Option>
                            <Option value="itinerary">行程</Option>
                            <Option value="accommodation">住宿</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item 
                        name="name" 
                        label="名稱"
                        extra="為這個代碼取一個容易記憶的名稱"
                    >
                        <Input placeholder="例如: 常用旅行社資料" style={{ maxWidth: 500 }} />
                    </Form.Item>
                </Form>

                <Divider orientation="left">區段內容</Divider>

                <div>
                    {renderSectionForm()}
                </div>

                <div style={{ marginTop: '24px', textAlign: 'right' }}>
                    <Space>
                        <Button onClick={() => navigate('/section-codes')}>
                            取消
                        </Button>
                        {!isNew && (
                            <Button 
                                danger 
                                icon={<DeleteOutlined />}
                                onClick={onDelete}
                            >
                                刪除
                            </Button>
                        )}
                        <Button 
                            type="primary" 
                            icon={<SaveOutlined />}
                            onClick={onSave}
                            loading={saving}
                        >
                            {isNew ? '新增' : '儲存'}
                        </Button>
                    </Space>
                </div>
            </Card>
        </div>
    );
}
